/**
 * /admin/collection-overlays — إدارة بطاقات overlay على بلوكات Collections في الهوم.
 *
 * كل صفّ = بطاقة overlay مرتبطة بمجموعة Shopify (handle) لسوق محدّد، مع نافذة
 * زمنية وأولويّة. تظهر هذه البطاقة فوق صورة المجموعة في `CollectionBlocks`.
 */
import * as React from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Loader2,
} from "lucide-react";
import { toast } from "@/lib/toast";
import { SiteShell } from "@/components/SiteShell";
import { AdminNav } from "@/components/admin/AdminNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAllCollectionOverlays } from "@/hooks/useCollectionOverlays";
import {
  upsertCollectionOverlay,
  deleteCollectionOverlay,
  OVERLAY_MARKETS,
  type CollectionOverlay,
  type OverlayMarket,
} from "@/lib/admin/collectionOverlays.functions";
import { fetchCollections } from "@/lib/shopify";
import { useLocale } from "@/contexts/LocaleContext";
import { TranslateBtn } from "@/components/admin/TranslateBtn";

export const Route = createFileRoute("/admin/collection-overlays")({
  validateSearch: (search: Record<string, unknown>): { from?: "home-blocks"; handle?: string; device?: "desktop" | "mobile" } => {
    const from = search.from === "home-blocks" ? "home-blocks" : undefined;
    const device = search.device === "desktop" || search.device === "mobile" ? search.device : undefined;
    return {
      from,
      device,
      handle: typeof search.handle === "string" ? search.handle : undefined,
    };
  },
  component: AdminCollectionOverlaysPage,
  head: () => ({
    meta: [
      { title: "Collection Overlays · Lecmo Admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
});

const MARKET_LABELS: Record<OverlayMarket, string> = {
  "*": "🌍 الكلّ / ROW",
  SA: "🇸🇦 السعوديّة",
  AE: "🇦🇪 الإمارات",
  IQ: "🇮🇶 العراق",
  OM: "🇴🇲 عُمان",
  US: "🇺🇸 الولايات المتحدة",
  KW: "🇰🇼 الكويت",
  QA: "🇶🇦 قطر",
  BH: "🇧🇭 البحرين",
};

type Status = "active" | "scheduled" | "ended" | "disabled";

function getStatus(c: CollectionOverlay): Status {
  if (!c.enabled) return "disabled";
  const now = Date.now();
  if (c.starts_at && new Date(c.starts_at).getTime() > now) return "scheduled";
  if (c.ends_at && new Date(c.ends_at).getTime() <= now) return "ended";
  return "active";
}

function StatusBadge({ s }: { s: Status }) {
  const map: Record<Status, { label: string; cls: string }> = {
    active: { label: "🟢 نشطة", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" },
    scheduled: { label: "🕐 مجدولة", cls: "bg-blue-500/15 text-blue-700 dark:text-blue-400" },
    ended: { label: "⚪ منتهية", cls: "bg-muted text-muted-foreground" },
    disabled: { label: "🔴 معطّلة", cls: "bg-destructive/15 text-destructive" },
  };
  const info = map[s];
  return (
    <Badge variant="outline" className={`border-0 ${info.cls}`}>
      {info.label}
    </Badge>
  );
}

function fmt(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("ar-SA", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function toLocalInput(iso: string | null): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
}

type FormState = {
  id?: string;
  collection_handle: string;
  name: string;
  market: OverlayMarket;
  starts_at: string;
  ends_at: string;
  priority: number;
  enabled: boolean;
  title_ar: string;
  title_en: string;
  subtitle_ar: string;
  subtitle_en: string;
  cta_label_ar: string;
  cta_label_en: string;
  cta_href: string;
};

function emptyForm(): FormState {
  return {
    collection_handle: "",
    name: "",
    market: "*",
    starts_at: "",
    ends_at: "",
    priority: 0,
    enabled: true,
    title_ar: "",
    title_en: "",
    subtitle_ar: "",
    subtitle_en: "",
    cta_label_ar: "",
    cta_label_en: "",
    cta_href: "",
  };
}

function toForm(c: CollectionOverlay): FormState {
  return {
    id: c.id,
    collection_handle: c.collection_handle,
    name: c.name,
    market: c.market,
    starts_at: toLocalInput(c.starts_at),
    ends_at: toLocalInput(c.ends_at),
    priority: c.priority,
    enabled: c.enabled,
    title_ar: c.title_ar ?? "",
    title_en: c.title_en ?? "",
    subtitle_ar: c.subtitle_ar ?? "",
    subtitle_en: c.subtitle_en ?? "",
    cta_label_ar: c.cta_label_ar ?? "",
    cta_label_en: c.cta_label_en ?? "",
    cta_href: c.cta_href ?? "",
  };
}

function AdminCollectionOverlaysPage() {
  const { locale } = useLocale();
  const isAr = locale === "ar";
  const search = Route.useSearch();
  const qc = useQueryClient();
  const { data: rows, isLoading } = useAllCollectionOverlays();
  const upsertFn = useServerFn(upsertCollectionOverlay);
  const deleteFn = useServerFn(deleteCollectionOverlay);

  const { data: collections } = useQuery({
    queryKey: ["admin-collections-list"],
    queryFn: () => fetchCollections(50),
    staleTime: 5 * 60_000,
  });

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [form, setForm] = React.useState<FormState>(emptyForm);
  const [saving, setSaving] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState<CollectionOverlay | null>(null);
  const [deleting, setDeleting] = React.useState(false);
  const sourceHandle = search.from === "home-blocks" ? search.handle : undefined;
  const autoOpenedRef = React.useRef<string | null>(null);
  const navigate = useNavigate();
  const returnToHomeBlocks = React.useCallback(() => {
    if (!sourceHandle) return;
    navigate({
      to: "/admin/home-blocks",
      search: { device: search.device, edit: sourceHandle } as never,
    });
  }, [navigate, search.device, sourceHandle]);

  const rowsForSource = React.useMemo(() => {
    if (!rows) return [];
    return sourceHandle ? rows.filter((row) => row.collection_handle === sourceHandle) : rows;
  }, [rows, sourceHandle]);

  function sourcePrefill(): FormState {
    if (!sourceHandle) return emptyForm();
    const title = collections?.find((c) => c.handle === sourceHandle)?.title ?? sourceHandle;
    return {
      ...emptyForm(),
      collection_handle: sourceHandle,
      name: title,
      cta_href: `/collection/${sourceHandle}`,
    };
  }

  function openNew() {
    setForm(sourcePrefill());
    setDialogOpen(true);
  }
  function openEdit(c: CollectionOverlay) {
    setForm(toForm(c));
    setDialogOpen(true);
  }

  React.useEffect(() => {
    if (!sourceHandle || isLoading || !rows) return;
    if (autoOpenedRef.current === sourceHandle) return;
    autoOpenedRef.current = sourceHandle;
    const matches = rows.filter((row) => row.collection_handle === sourceHandle);
    if (matches.length === 1) {
      openEdit(matches[0]);
      return;
    }
    if (matches.length === 0) {
      setForm(sourcePrefill());
      setDialogOpen(true);
    }
  }, [collections, isLoading, rows, sourceHandle]);

  async function handleSave() {
    if (!form.collection_handle.trim()) {
      toast.error("اختر مجموعة (handle) أوّلاً");
      return;
    }
    if (!form.name.trim()) {
      toast.error("اكتب اسماً داخليّاً للحملة");
      return;
    }
    setSaving(true);
    try {
      await upsertFn({
        data: {
          id: form.id,
          collection_handle: form.collection_handle.trim(),
          name: form.name.trim(),
          market: form.market,
          starts_at: form.starts_at || null,
          ends_at: form.ends_at || null,
          priority: Number(form.priority) || 0,
          enabled: form.enabled,
          title_ar: form.title_ar || null,
          title_en: form.title_en || null,
          subtitle_ar: form.subtitle_ar || null,
          subtitle_en: form.subtitle_en || null,
          cta_label_ar: form.cta_label_ar || null,
          cta_label_en: form.cta_label_en || null,
          cta_href: form.cta_href || null,
        },
      });
      toast.success(form.id ? "تمّ التحديث" : "تمّت الإضافة");
      setDialogOpen(false);
      await qc.invalidateQueries({ queryKey: ["collection-overlays"] });
      returnToHomeBlocks();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "تعذّر الحفظ");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await deleteFn({ data: { id: confirmDelete.id } });
      toast.success("تمّ الحذف");
      setConfirmDelete(null);
      await qc.invalidateQueries({ queryKey: ["collection-overlays"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "تعذّر الحذف");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <SiteShell>
      <div className="mx-auto w-full max-w-[1600px] px-4 py-6">
        <AdminNav />

        <div className="mb-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {sourceHandle ? (
              <Link
                to="/admin/home-blocks"
                search={{ device: search.device, edit: sourceHandle } as never}
                className="text-foreground/60 hover:text-foreground"
              >
                <ArrowLeft className="h-5 w-5 rtl:scale-x-[-1]" />
              </Link>
            ) : (
              <Link to="/admin" className="text-foreground/60 hover:text-foreground">
                <ArrowLeft className="h-5 w-5 rtl:scale-x-[-1]" />
              </Link>
            )}
            <div>
              <h1 className="t-h3 font-serif">بطاقات Overlay على المجموعات</h1>
              <p className="t-micro text-foreground/60">
                بطاقة نصّيّة + زرّ تظهر فوق صورة كلّ مجموعة في الهوم.
              </p>
            </div>
          </div>
          <Button onClick={openNew}>
            <Plus className="h-4 w-4" /> حملة جديدة
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-foreground/60">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : rowsForSource.length === 0 ? (
          <div className="rounded-none border border-dashed border-border p-10 text-center">
            <p className="t-body text-foreground/70">لا توجد بطاقات بعد.</p>
            <Button onClick={openNew} variant="outline" className="mt-4">
              <Plus className="h-4 w-4" /> أضف أوّل بطاقة
            </Button>
          </div>
        ) : (
          <div className="overflow-hidden rounded-none border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-foreground/70">
                <tr>
                  <th className="px-3 py-2 text-start">المجموعة</th>
                  <th className="px-3 py-2 text-start">الاسم</th>
                  <th className="px-3 py-2 text-start">السوق</th>
                  <th className="px-3 py-2 text-start">الحالة</th>
                  <th className="px-3 py-2 text-start">النافذة</th>
                  <th className="px-3 py-2 text-start">الأولويّة</th>
                  <th className="px-3 py-2 text-end">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {rowsForSource.map((c) => (
                  <tr key={c.id} className="border-t border-border">
                    <td className="px-3 py-2 font-mono text-xs">{c.collection_handle}</td>
                    <td className="px-3 py-2">{c.name}</td>
                    <td className="px-3 py-2">{MARKET_LABELS[c.market]}</td>
                    <td className="px-3 py-2">
                      <StatusBadge s={getStatus(c)} />
                    </td>
                    <td className="px-3 py-2 text-xs text-foreground/70">
                      {fmt(c.starts_at)} → {fmt(c.ends_at)}
                    </td>
                    <td className="px-3 py-2">{c.priority}</td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(c)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setConfirmDelete(c)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* محرّر */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) returnToHomeBlocks(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{form.id ? "تعديل بطاقة" : "بطاقة جديدة"}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-2 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>المجموعة (handle)</Label>
                {collections && collections.length > 0 ? (
                  <Select
                    value={form.collection_handle}
                    onValueChange={(v) => setForm((f) => ({ ...f, collection_handle: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="اختر مجموعة" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__hero__">✨ الهوم — فوق فيديو الهيرو</SelectItem>
                      {collections.map((c) => (
                        <SelectItem key={c.handle} value={c.handle}>
                          {c.title} — {c.handle}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={form.collection_handle}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, collection_handle: e.target.value }))
                    }
                    placeholder="for-her"
                  />
                )}
              </div>

              <div>
                <Label>السوق</Label>
                <Select
                  value={form.market}
                  onValueChange={(v) => setForm((f) => ({ ...f, market: v as OverlayMarket }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {OVERLAY_MARKETS.map((m) => (
                      <SelectItem key={m} value={m}>
                        {MARKET_LABELS[m]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>اسم داخليّ للحملة</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="مثلاً: عرض رمضان للنسائيّ"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="flex items-center justify-between">
                  <Label>العنوان (عربي)</Label>
                  <TranslateBtn source={form.title_en} destination={form.title_ar} from="en" to="ar" kind="title" isAr={isAr} onResult={(t) => setForm((f) => ({ ...f, title_ar: t }))} />
                </div>
                <Input
                  value={form.title_ar}
                  onChange={(e) => setForm((f) => ({ ...f, title_ar: e.target.value }))}
                  placeholder="إصدار محدود"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Title (English)</Label>
                  <TranslateBtn source={form.title_ar} destination={form.title_en} from="ar" to="en" kind="title" isAr={isAr} onResult={(t) => setForm((f) => ({ ...f, title_en: t }))} />
                </div>
                <Input
                  value={form.title_en}
                  onChange={(e) => setForm((f) => ({ ...f, title_en: e.target.value }))}
                  placeholder="Limited edition"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="flex items-center justify-between">
                  <Label>الوصف (عربي)</Label>
                  <TranslateBtn source={form.subtitle_en} destination={form.subtitle_ar} from="en" to="ar" kind="body" isAr={isAr} onResult={(t) => setForm((f) => ({ ...f, subtitle_ar: t }))} />
                </div>
                <Textarea
                  rows={2}
                  value={form.subtitle_ar}
                  onChange={(e) => setForm((f) => ({ ...f, subtitle_ar: e.target.value }))}
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Subtitle (English)</Label>
                  <TranslateBtn source={form.subtitle_ar} destination={form.subtitle_en} from="ar" to="en" kind="body" isAr={isAr} onResult={(t) => setForm((f) => ({ ...f, subtitle_en: t }))} />
                </div>
                <Textarea
                  rows={2}
                  value={form.subtitle_en}
                  onChange={(e) => setForm((f) => ({ ...f, subtitle_en: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <div className="flex items-center justify-between">
                  <Label>نصّ الزرّ (عربي)</Label>
                  <TranslateBtn source={form.cta_label_en} destination={form.cta_label_ar} from="en" to="ar" kind="cta" isAr={isAr} onResult={(t) => setForm((f) => ({ ...f, cta_label_ar: t }))} />
                </div>
                <Input
                  value={form.cta_label_ar}
                  onChange={(e) => setForm((f) => ({ ...f, cta_label_ar: e.target.value }))}
                  placeholder="اكتشف الآن"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Button (English)</Label>
                  <TranslateBtn source={form.cta_label_ar} destination={form.cta_label_en} from="ar" to="en" kind="cta" isAr={isAr} onResult={(t) => setForm((f) => ({ ...f, cta_label_en: t }))} />
                </div>
                <Input
                  value={form.cta_label_en}
                  onChange={(e) => setForm((f) => ({ ...f, cta_label_en: e.target.value }))}
                  placeholder="Discover"
                />
              </div>
              <div>
                <Label>رابط الزرّ</Label>
                <Input
                  value={form.cta_href}
                  onChange={(e) => setForm((f) => ({ ...f, cta_href: e.target.value }))}
                  placeholder="/collection/for-her"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>يبدأ في</Label>
                <Input
                  type="datetime-local"
                  value={form.starts_at}
                  onChange={(e) => setForm((f) => ({ ...f, starts_at: e.target.value }))}
                />
              </div>
              <div>
                <Label>ينتهي في</Label>
                <Input
                  type="datetime-local"
                  value={form.ends_at}
                  onChange={(e) => setForm((f) => ({ ...f, ends_at: e.target.value }))}
                />
              </div>
              <div>
                <Label>الأولويّة (0-1000)</Label>
                <Input
                  type="number"
                  min={0}
                  max={1000}
                  value={form.priority}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, priority: Number(e.target.value) || 0 }))
                  }
                />
              </div>
            </div>

            <div className="flex items-center justify-between rounded-none border border-border p-3">
              <div>
                <Label className="cursor-pointer">مفعّلة</Label>
                <p className="t-micro text-foreground/60">
                  عند التعطيل لا تظهر البطاقة حتى لو كانت ضمن النافذة الزمنيّة.
                </p>
              </div>
              <Switch
                checked={form.enabled}
                onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => { setDialogOpen(false); returnToHomeBlocks(); }} disabled={saving}>
              إلغاء
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} حفظ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>حذف البطاقة؟</AlertDialogTitle>
            <AlertDialogDescription>
              لا يمكن التراجع. هل تريد حذف «{confirmDelete?.name}»؟
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>إلغاء</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting}>
              {deleting && <Loader2 className="h-4 w-4 animate-spin" />} حذف
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SiteShell>
  );
}
